import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, 
  Clock, 
  Calendar,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Play,
  History,
  FileText,
  FileX,
  Users,
  StickyNote,
  Globe
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import ConfirmationModal from '../modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import TimezoneSelector from '../appointments/TimezoneSelector';

const AutoCleanupSettings = () => {
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    schedule_type: 'daily', // daily, weekly, monthly, custom
    frequency: 1,
    day_of_week: 1, // 0-6 (Sunday-Saturday)
    day_of_month: 1, // 1-31
    time: '03:00',
    custom_date: '',
    is_recurring: true,
    cleanup_after_days: 30,
    include_manual_cvs: true,
    include_offer_cvs: true,
    include_results: true,
    include_unshared_notes: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris'
  });
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      // D'abord récupérer le timezone de l'utilisateur depuis ses settings
      const userResponse = await fetch('/api/settings/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      let userTimezone = 'Europe/Paris';
      if (userResponse.ok) {
        const userData = await userResponse.json();
        userTimezone = userData.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
      }
      
      // Ensuite récupérer les paramètres de nettoyage
      const response = await fetch('/api/settings/cleanup', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Utiliser le timezone de l'utilisateur ou celui détecté
        data.timezone = data.timezone || userTimezone;
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching cleanup settings:', error);
      toast.error(t('settings.preferences.automaticCleanup.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchJobs = useCallback(async () => {
    try {
      setLoadingJobs(true);
      const response = await fetch('/api/jobs/cleanup', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching cleanup jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchJobs();
  }, [fetchJobs, fetchSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch('/api/settings/cleanup', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success(t('settings.preferences.automaticCleanup.success.saved'));
        fetchJobs(); // Rafraîchir les jobs
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving cleanup settings:', error);
      toast.error(t('settings.preferences.automaticCleanup.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleManualCleanup = async () => {
    const result = await confirm({
      title: t('settings.preferences.automaticCleanup.actions.runNow'),
      message: t('settings.preferences.automaticCleanup.confirmRunNow'),
      type: 'warning',
      confirmText: t('settings.preferences.automaticCleanup.actions.runNow'),
      cancelText: t('common.cancel')
    });
    
    if (!result) {
      return;
    }

    try {
      const response = await fetch('/api/jobs/cleanup/manual', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cleanup_after_days: settings.cleanup_after_days,
          include_manual_cvs: settings.include_manual_cvs,
          include_offer_cvs: settings.include_offer_cvs,
          include_results: settings.include_results,
          include_unshared_notes: settings.include_unshared_notes
        })
      });
      
      if (response.ok) {
        toast.success(t('settings.preferences.automaticCleanup.success.started'));
        fetchJobs();
      } else {
        throw new Error('Failed to start manual cleanup');
      }
    } catch (error) {
      console.error('Error starting manual cleanup:', error);
      toast.error(t('settings.preferences.automaticCleanup.error.start'));
    }
  };

  const getScheduleDescription = () => {
    const { schedule_type, frequency, day_of_week, day_of_month, time, custom_date, is_recurring, timezone } = settings;
    
    const timeWithTz = `${time} (${timezone})`;
    
    switch (schedule_type) {
      case 'daily':
        return frequency === 1 
          ? t('settings.preferences.automaticCleanup.frequency.everyDay') + ' à ' + timeWithTz
          : t('settings.preferences.automaticCleanup.frequency.everyXDays', { count: frequency }) + ' à ' + timeWithTz;
      
      case 'weekly':
        const dayNames = {
          0: t('settings.preferences.automaticCleanup.dayOfWeek.sunday'),
          1: t('settings.preferences.automaticCleanup.dayOfWeek.monday'),
          2: t('settings.preferences.automaticCleanup.dayOfWeek.tuesday'),
          3: t('settings.preferences.automaticCleanup.dayOfWeek.wednesday'),
          4: t('settings.preferences.automaticCleanup.dayOfWeek.thursday'),
          5: t('settings.preferences.automaticCleanup.dayOfWeek.friday'),
          6: t('settings.preferences.automaticCleanup.dayOfWeek.saturday')
        };
        return dayNames[day_of_week] + ' à ' + timeWithTz;
      
      case 'monthly':
        return t('settings.preferences.automaticCleanup.dayOfMonth.day', { day: day_of_month }) + ' à ' + timeWithTz;
      
      case 'custom':
        if (custom_date) {
          // Ne pas faire de conversion, afficher tel quel
          const date = new Date(custom_date);
          const options = { 
            year: 'numeric', 
            month: 'numeric', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          };
          return is_recurring 
            ? `${date.toLocaleString(undefined, options)} (${timezone}) (${t('settings.preferences.automaticCleanup.recurring.yes')})`
            : `${date.toLocaleString(undefined, options)} (${timezone}) (${t('settings.preferences.automaticCleanup.recurring.no')})`;
        }
        return '-';
      
      default:
        return '';
    }
  };

  const getJobStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {t(`settings.preferences.automaticCleanup.jobHistory.status.${status}`)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="settings-section-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg mr-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('settings.preferences.automaticCleanup.title')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.preferences.automaticCleanup.subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">
                {settings.enabled ? t('common.enabled', 'Activé') : t('common.disabled', 'Désactivé')}
              </span>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
                aria-label={settings.enabled ? t('common.enabled', 'Enabled') : t('common.disabled', 'Disabled')}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                    settings.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

      <AnimatePresence>
        {settings.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-6"
          >
            {/* Configuration de la planification */}
            <div className="settings-section-panel">
              <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-gray-600" />
                {t('settings.preferences.automaticCleanup.schedule')}
              </h4>

              <div className="space-y-4">
                {/* Type de planification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.preferences.automaticCleanup.scheduleType.label')}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['daily', 'weekly', 'monthly', 'custom'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSettings({ ...settings, schedule_type: type })}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          settings.schedule_type === type
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {t(`settings.preferences.automaticCleanup.scheduleType.${type}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Configuration selon le type */}
                {settings.schedule_type === 'daily' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.frequency.label')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={settings.frequency}
                        onChange={(e) => setSettings({ ...settings, frequency: parseInt(e.target.value) || 1 })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.time.label')}
                      </label>
                      <input
                        type="time"
                        value={settings.time}
                        onChange={(e) => setSettings({ ...settings, time: e.target.value })}
                        className="input"
                      />
                    </div>
                  </div>
                )}

                {settings.schedule_type === 'weekly' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.dayOfWeek.label')}
                      </label>
                      <select
                        value={settings.day_of_week}
                        onChange={(e) => setSettings({ ...settings, day_of_week: parseInt(e.target.value) })}
                        className="input"
                      >
                        <option value="0">{t('days.sunday', 'Dimanche')}</option>
                        <option value="1">{t('days.monday', 'Lundi')}</option>
                        <option value="2">{t('days.tuesday', 'Mardi')}</option>
                        <option value="3">{t('days.wednesday', 'Mercredi')}</option>
                        <option value="4">{t('days.thursday', 'Jeudi')}</option>
                        <option value="5">{t('days.friday', 'Vendredi')}</option>
                        <option value="6">{t('days.saturday', 'Samedi')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.time.label')}
                      </label>
                      <input
                        type="time"
                        value={settings.time}
                        onChange={(e) => setSettings({ ...settings, time: e.target.value })}
                        className="input"
                      />
                    </div>
                  </div>
                )}

                {settings.schedule_type === 'monthly' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.dayOfMonth.label')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={settings.day_of_month}
                        onChange={(e) => setSettings({ ...settings, day_of_month: parseInt(e.target.value) || 1 })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.time.label')}
                      </label>
                      <input
                        type="time"
                        value={settings.time}
                        onChange={(e) => setSettings({ ...settings, time: e.target.value })}
                        className="input"
                      />
                    </div>
                  </div>
                )}

                {settings.schedule_type === 'custom' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.preferences.automaticCleanup.customDate.label')}
                      </label>
                      <input
                        type="datetime-local"
                        value={settings.custom_date}
                        onChange={(e) => setSettings({ ...settings, custom_date: e.target.value })}
                        className="input"
                      />
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.is_recurring}
                        onChange={(e) => setSettings({ ...settings, is_recurring: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {t('settings.preferences.automaticCleanup.recurring.label')}
                      </span>
                    </label>
                  </div>
                )}

                {/* Fuseau horaire */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    {t('settings.preferences.automaticCleanup.timezone.label', 'Fuseau horaire')}
                  </label>
                  <TimezoneSelector
                    value={settings.timezone}
                    onChange={(value) => setSettings({ ...settings, timezone: value })}
                    showLabel={false}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.preferences.automaticCleanup.timezone.description', 'Le nettoyage sera exécuté selon ce fuseau horaire')}
                  </p>
                </div>

                {/* Résumé de la planification */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-gray-600 mr-2" />
                    <span className="text-sm font-medium text-gray-700">
                      {getScheduleDescription()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration du nettoyage */}
            <div className="settings-section-panel">
              <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                <FileX className="w-5 h-5 mr-2 text-gray-600" />
                {t('settings.preferences.automaticCleanup.cleanupOptions.title')}
              </h4>

              <div className="space-y-4">
                {/* Délai de conservation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {t('settings.preferences.automaticCleanup.cleanupOptions.daysOld.label')}
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="1"
                      max="365"
                      value={settings.cleanup_after_days}
                      onChange={(e) => setSettings({ ...settings, cleanup_after_days: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-gray-700 min-w-[80px]">
                      {settings.cleanup_after_days} {t(settings.cleanup_after_days > 1 ? 'common.days' : 'common.day', 
                        settings.cleanup_after_days > 1 ? 'jours' : 'jour')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Les fichiers plus anciens que cette durée seront supprimés
                  </p>
                </div>

                {/* Types de fichiers */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileX className="w-4 h-4 inline mr-1" />
                    {t('settings.preferences.automaticCleanup.cleanupOptions.fileTypes.label')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.include_manual_cvs}
                        onChange={(e) => setSettings({ ...settings, include_manual_cvs: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <FileText className="w-4 h-4 text-gray-500 ml-3 mr-2" />
                      <span className="text-sm text-gray-700">
                        CVs uploadés manuellement
                      </span>
                    </label>
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.include_offer_cvs}
                        onChange={(e) => setSettings({ ...settings, include_offer_cvs: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <Users className="w-4 h-4 text-gray-500 ml-3 mr-2" />
                      <span className="text-sm text-gray-700">
                        CVs soumis via offres et leurs candidatures
                      </span>
                    </label>
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.include_results}
                        onChange={(e) => setSettings({ ...settings, include_results: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <CheckCircle className="w-4 h-4 text-gray-500 ml-3 mr-2" />
                      <span className="text-sm text-gray-700">
                        Résultats d'analyse IA
                      </span>
                    </label>
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.include_unshared_notes}
                        onChange={(e) => setSettings({ ...settings, include_unshared_notes: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <StickyNote className="w-4 h-4 text-gray-500 ml-3 mr-2" />
                      <span className="text-sm text-gray-700">
                        Notes non partagées
                      </span>
                    </label>
                  </div>
                </div>

                {/* Action immédiate */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {t('settings.preferences.automaticCleanup.actions.runNow')}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('settings.preferences.automaticCleanup.actions.runNowDescription')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleManualCleanup}
                      className="btn-secondary"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {t('settings.preferences.automaticCleanup.actions.runNow')}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

        {/* Actions */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {t('common.saving', 'Enregistrement...')}
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('common.save', 'Enregistrer')}
              </>
            )}
          </button>
        </div>

        {/* Historique des jobs */}
        <div className="settings-section-panel">
        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
          <History className="w-5 h-5 mr-2 text-gray-600" />
          {t('settings.preferences.automaticCleanup.jobHistory.title')}
        </h4>

        {loadingJobs ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8">
            <FileX className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {t('settings.preferences.automaticCleanup.jobHistory.noHistory')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(job.created_at).toLocaleString(undefined, { timeZone: settings.timezone })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.payload?.manual ? t('settings.preferences.automaticCleanup.jobHistory.details.manual') : t('settings.preferences.automaticCleanup.jobHistory.details.scheduled')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getJobStatusBadge(job.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmProps} />
    </>
  );
};

export default AutoCleanupSettings;
